import { createContext, useCallback, useContext, useState } from 'react';
import uniqueId from 'lodash/uniqueId';

import { NotificationOptions, Notification } from './types';
import { Notifications } from './Notifications';

type Props = {
  children: React.ReactNode;
  notificationTimeout?: number;
};

type NotificationContextType = {
  clearNotification: (id: string) => void;
  createNotification: (options: NotificationOptions) => void;
};

const defaultNotificationContext = {
  clearNotification: function () {
    // noop
  },
  createNotification: function () {
    // noop
  },
};

const NotificationContext = createContext<NotificationContextType>(
  defaultNotificationContext
);

export function useNotification(): NotificationContextType {
  return useContext(NotificationContext);
}

export function NotificationProvider(props: Props) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const clearNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const createNotification = useCallback((options: NotificationOptions) => {
    setNotifications(prev => [
      ...prev,
      { ...options, id: uniqueId('notification-') },
    ]);
  }, []);

  const notificationTimeout = props.notificationTimeout || 5000;

  const value = {
    clearNotification,
    createNotification,
  };

  return (
    <>
      <Notifications
        notifications={notifications}
        onDismiss={clearNotification}
        timeout={notificationTimeout}
      />
      <NotificationContext.Provider value={value}>
        {props.children}
      </NotificationContext.Provider>
    </>
  );
}
